import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import TextArea from '@atlaskit/textarea';
import InlineEdit from '@atlaskit/inline-edit';
import InlineDialog from '@atlaskit/inline-dialog';
import Spinner from '@atlaskit/spinner';
import { R300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import EditIcon from '@atlaskit/icon/glyph/edit';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { AnnotateEventRequest } from '@atlassiansox/phobos-client-ts';

import { apis, apiResolve } from '@/services';
import { useFetchErrorController } from '@/controllers';
import { stringReplaceLink } from '@/utils';

import * as styles from './styles';
import { validateAnnotation } from './utils';
import { EventAnnotationsProps } from './types';
import { testIds } from './constants';

const EventAnnotations: React.FC<EventAnnotationsProps> = ({
  event,
  readonly = false,
}: EventAnnotationsProps) => {
  const { publicId } = event;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsloading] = useState<boolean>(false);
  const [annotation, setAnnotation] = useState<string>(event.annotation || '');

  useEffect(() => {
    setAnnotation(event.annotation || '');
  }, [event.annotation]);

  const queryClient = useQueryClient();
  const { onError } = useFetchErrorController();
  const mutation = useMutation(
    (request: AnnotateEventRequest) => apiResolve(apis.event.annotateEvent(request)),
    {
      onError,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['events'] });
        setIsEditing(false);
        setIsloading(false);
      },
    }
  );

  const handleEditClick = () => {
    if (isLoading) return;
    setIsEditing(!isEditing);
  };

  const editorViewStyles = annotation ? {} : styles.annotationView;
  const editorStyles = isEditing ? styles.annotationEdit : editorViewStyles;

  const readView = (
    <div data-testid={testIds.annotationView} css={styles.readViewContainer}>
      {stringReplaceLink(annotation)}
    </div>
  );

  if (readonly) return readView;

  return (
    <div css={styles.annotationColumn}>
      <div css={editorStyles}>
        <InlineEdit
          isEditing={isEditing}
          defaultValue={annotation}
          editView={({ ...fieldProps }, ref) => {
            const { errorMessage, isDisabled, ...rest } = fieldProps;
            const disabled = isDisabled || isLoading;
            return (
              <InlineDialog
                isOpen={fieldProps.isInvalid}
                content={
                  <div css={styles.annotationErrorMessage} data-testid={testIds.textareaMessage}>
                    <ErrorIcon primaryColor={token('color.text.danger', R300)} label="Error" />
                    {errorMessage}
                  </div>
                }
                placement="bottom-start"
              >
                {/* @ts-ignore */}
                <TextArea
                  {...rest}
                  minimumRows={1}
                  isDisabled={disabled}
                  ref={ref}
                  testId={testIds.annotationEdit}
                  onKeyPress={e => {
                    if (e.key === 'Enter') ref.current.blur();
                  }}
                />
              </InlineDialog>
            );
          }}
          readView={() => readView}
          onConfirm={(value: string) => {
            // Optimistically update UI
            setAnnotation(value);
            setIsloading(true);
            mutation.mutate({
              publicId,
              eventAnnotationInput: {
                content: value,
              },
            });
          }}
          onCancel={() => {
            setIsEditing(false);
          }}
          onEdit={() => {
            setIsEditing(true);
          }}
          keepEditViewOpenOnBlur={false}
          validate={validateAnnotation}
        />
      </div>

      <div css={styles.editIcon} onClick={handleEditClick} data-testid={testIds.btnEdit}>
        {isLoading && <Spinner />}
        {!isLoading && <EditIcon size="small" label="Edit" />}
      </div>
    </div>
  );
};

export default EventAnnotations;
